import React, { useState, useEffect, useRef, useCallback } from 'react';
import NavBar from '../NavBar';
import FinalizedCoursesTwo from './FinalizedCoursesTwo';
import { db, auth } from '../firebase';
import { getDatabase, ref, get } from 'firebase/database';
import { doc, getDoc, updateDoc } from "firebase/firestore";
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { useAuth } from '../../contexts/AuthContext';
import './MajorPage.css';
import { FaGraduationCap, FaBookOpen, FaTimes } from 'react-icons/fa';
import { FaBookBookmark, FaBook } from 'react-icons/fa6';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { toast } from 'react-toastify';
import IncompleteDataWarningModal from './IncomplieteDataWarningModal';
import { fetchElectivesFromDatabase } from './utils/fetchElectives';
import { fetchMilestonesFromDatabase } from './utils/fetchMilestones';
import { fetchCoursesFromDatabase } from './utils/fetchCourses'; 

// Import the useTranslation hook
import { useTranslation } from 'react-i18next';

const MajorPage = () => {
  // Initialize the translation hook
  const { t } = useTranslation();
  function fixToJSON(str) {
    return str.replace(/(\{|,)\s*([0-9]+)\s*:/g, '$1 "$2":');
  }
  // Assume fetchedTerms comes from the Auth context (as a string)
  const { fetchedTerms } = useAuth();
  let parsedTerms = {}
  
  if (fetchedTerms && typeof fetchedTerms === 'string') {
    const validJSONString = fixToJSON(fetchedTerms);
    parsedTerms = JSON.parse(validJSONString);
  }
  // Create a termMap where keys are numbers (as strings) and values are friendly names.
  const termMap = {};
  Object.keys(parsedTerms).forEach(key => {
    termMap[key] = parsedTerms[key];
  });
  console.log('Term Map: ', termMap);
  // Get sorted term keys (as strings, sorted numerically) and derive the friendly names.
  const sortedTermKeys = Object.keys(termMap).sort((a, b) => Number(a) - Number(b));
  console.log('sortedTermKeys: ', sortedTermKeys);
  const dynamicTerms = sortedTermKeys.map(key => termMap[key]);  

  const [currentUser, setCurrentUser] = useState(null);
  const [maxCourseYear, setMaxCourseYear] = useState(4); // Initialize with default value
  const [numYears, setNumYears] = useState(4);
  const [selectedDegreeCourses, setSelectedDegreeCourses] = useState([]);
  const [middleschoolClasses, setMiddleschoolClasses] = useState([]);
  const [selectedMinorCourses, setSelectedMinorCourses] = useState([]);
  const [selectedMinor, setSelectedMinor] = useState([]);
  const [showSidebar, setShowSidebar] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [showDegreePlanSearchModal, setShowDegreePlanSearchModal] = useState(false);
  const [selectedDegree, setSelectedDegree] = useState('');
  const [selectedElCat, setSelectedElCat] = useState(null);
  const [faculties, setFaculties] = useState([]);
  const [selectedFaculty, setSelectedFaculty] = useState(null);
  const [showIncompleteDataWarningModal, setShowIncompleteDataWarningModal] = useState(false);
  const [finalizedOrganizedCourses, setFinalizedOrganizedCourses] = useState({});

  const {
    fetchedCourseData,
    fetchedElective,
    fetchedMinors,
    fetchedMajors,
    fetchedMajorRecs,
    pathwayGroupingsData,
  } = useAuth();
    console.log('FetchedCourseData: ', fetchedCourseData);
  const [surveyAnswers, setSurveyAnswers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [checkedMilestones, setCheckedMilestones] = useState({});

  const [transformedMajorRecData, setTransformedMajorRecData] = useState([]);
  useEffect(() => {
    if (fetchedMajorRecs) {
      const transformedMajorRecData = fetchedMajorRecs.reduce((acc, curr) => {
        const { Major, ...categories } = curr;
        acc[Major] = categories;
        return acc;
      }, {});
      setTransformedMajorRecData(transformedMajorRecData);
    }
  }, [fetchedMajorRecs]);

  useEffect(() => {
    const checkTutorialStatus = async () => {
      if (currentUser) {
        const userDocRef = doc(db, 'users', currentUser.uid);
        const userDoc = await getDoc(userDocRef);
        if (userDoc.exists()) {
          let hasSeenPathwayWarning = userDoc.data().hasSeenPathwayWarning || false;
          if (!hasSeenPathwayWarning) {
            setShowIncompleteDataWarningModal(true);
          }
        }
      }
    };

    checkTutorialStatus();
  }, [currentUser]);


  const handleCloseIncompleteDataWarningModal = async () => {
    setShowIncompleteDataWarningModal(false)
    const userDocRef = doc(db, 'users', currentUser.uid);
    const userDoc = await getDoc(userDocRef);
    if (userDoc.exists()) {
      await updateDoc(userDocRef, {'hasSeenPathwayWarning': true});
    }
  }

  const fetchDegreesFromDatabase = async () => {
    try {
      const storedData = JSON.parse(localStorage.getItem('degreesAndFaculties'));
      if (storedData) {
        console.log(t('majorPage.dataRetrievedFromLocalStorage', 'Data retrieved from local storage'));
        return storedData;
      }

      const courseIds = [];
      const uniqueFaculties = new Set();

      const fetchCoursesFromSheet = async (projectAndCollectionId) => {
        const dbRef = ref(getDatabase(), projectAndCollectionId);
        const coursesSnapshot = await get(dbRef);
        coursesSnapshot.forEach((courseSnapshot) => {
          const courseData = courseSnapshot.val();
          courseIds.push({
            Major: courseData.pathwayName,
            Faculty: courseData.Faculty,
          });
          const faculty = courseData.Faculty.split('||');
          faculty.forEach((fac) => uniqueFaculties.add(fac));
        });
      };

      await fetchCoursesFromSheet(
        '16zznokkTgjsqu1rYYo5rD8dES40-9dq1guLOAYA0cqI/Pathway_Groupings'
      );

      const degreesAndFaculties = {
        courseIds,
        uniqueFaculties: Array.from(uniqueFaculties),
      };
      localStorage.setItem('degreesAndFaculties', JSON.stringify(degreesAndFaculties));

      return degreesAndFaculties;
    } catch (error) {
      console.error(t('majorPage.errorFetchingDegreesAndFaculties', 'Error fetching degrees and faculties from the Realtime Database:'), error);
      return { courseIds: [], uniqueFaculties: [] };
    }
  };

  const [degs, setDegs] = useState([]);

  useEffect(() => {
    const fetchDegreeData = async () => {
      try {
        const { courseIds, uniqueFaculties } = await fetchDegreesFromDatabase();
        setDegs(courseIds);
        setFaculties(uniqueFaculties);
      } catch (error) {
        console.error(t('majorPage.degreesAndFacultiesNotFound', 'Degrees and faculties not found'));
      }
    };
    fetchDegreeData();
  }, []);

  const handleFacultyFilterChange = (faculty) => {
    setSelectedFaculty(faculty);
    filterDegrees(searchQuery, faculty);
  };

  const [tabs, setTabs] = useState(() => {
    const storedTabs = localStorage.getItem('tabs');
    return storedTabs ? JSON.parse(storedTabs) : [];
  });

  const [selectedMajor1, setSelectedMajor] = useState([]);
  const [activeTab, setActiveTab] = useState(0);
  const [activeTabCourses, setActiveTabCourses] = useState([]);
  const [electiveData, setElectiveData] = useState([]);
  const [selectedElectiveCategory, setSelectedElectiveCategory] = useState(null);
  const [selectedSecondMajor, setSelectedSecondMajor] = useState([]);

  useEffect(() => {
    localStorage.setItem('tabs', JSON.stringify(tabs));
  }, [tabs]);

  const [filteredFetchedMajors, setFilteredFetchedMajors] = useState({});

  useEffect(() => {
    if (fetchedMajors) {
      // Create a copy of fetchedMajors without the "Electives" key
      const { Electives, ...filteredMajors } = fetchedMajors;
      setFilteredFetchedMajors(filteredMajors);
    }
  }, [fetchedMajors]);

  const filterDegrees = (query, faculty) => {
    const filtered = Object.keys(fetchedMajors)
      .filter((major) => {
        const matchesQuery = major.toLowerCase().includes(query.toLowerCase());
        const matchesFaculty =
          !faculty || degs.some((degree) => degree.Major === major && degree.Faculty.includes(faculty));
        return matchesQuery && matchesFaculty;
      })
      .reduce((obj, key) => {
        // Only add the key to the result if it is not "Electives"
        if (key !== 'Electives') {
          obj[key] = fetchedMajors[key];
        }
        return obj;
      }, {});
  
    setFilteredFetchedMajors(filtered);
  };
  

  const handleSearchQueryChange = (e) => {
    const newQuery = e.target.value;
    setSearchQuery(newQuery);
    filterDegrees(newQuery, selectedFaculty);
  };

  const [filteredMinors, setFilteredMinors] = useState({});
  useEffect(() => {
    if (fetchedMinors) {
      setFilteredMinors(fetchedMinors);
    }
  }, [fetchedMinors]);

  const filterMinors = (query) => {
    const filtered = Object.keys(fetchedMinors)
      .filter((major) => major.toLowerCase().includes(query.toLowerCase()))
      .reduce((obj, key) => {
        obj[key] = fetchedMinors[key];
        return obj;
      }, {});

    setFilteredMinors(filtered);
  };

  const handleDegreeSelection = async (degree) => {
    try {
      
      console.log(t('majorPage.selectedDegree', 'Selected Degree:'), degree.Major, degree);
      setTabs((prevTabs) => {
        const updatedTabs = [...prevTabs, degree.Major];
        setActiveTab(updatedTabs.length - 1);
        return updatedTabs;
      });
      setSelectedDegree(degree.Major);
      setShowDegreePlanSearchModal(false);

      const courses = await fetchAllMajorsCourses(degree.Major);
      setActiveTabCourses(courses);
      const fetchedElectives = fetchElectivesFromDatabase(degree.Major, fetchedElective);
      setElectiveData(fetchedElectives);
      const maxYear = courses.reduce((max, course) => Math.max(max, course.courseYear || 1), 1);
      setMaxCourseYear(prevMax => Math.max(prevMax, maxYear));
    } catch (error) {
      console.error(t('majorPage.errorFetchingDegreeData', 'Error fetching degree data:'), error);
    }
  };

  const getUserKeptCourses = (finalizedSchedule) => {
    const keptCourses = [];
    for (const year in finalizedSchedule) {
      for (const sem in finalizedSchedule[year]) {
        if (finalizedSchedule[year][sem].courses) {
          keptCourses.push(...finalizedSchedule[year][sem].courses);
        }
      }
    }
    console.log('Kept courses: ', keptCourses);
    return keptCourses;
  };

  const getUserKeptMilestones = (finalizedMilestones) => {
    const keptMilestones = [];
    for (const year in finalizedMilestones) {
      for (const semester in finalizedMilestones[year]) {
        const semesterData = finalizedMilestones[year][semester];
        if (semesterData.milestones) {
          semesterData.milestones.forEach((milestone) => {
            keptMilestones.push(milestone.Description);
          });
        }
      }
    }
    return keptMilestones;
  };

  const [milestones, setMilestones] = useState([]);
  const [completedMilestones, setCompletedMilestones] = useState([]);

  const handleSecondMajorSelection = async (degree) => {
    try {
      if (degree) {
        if (selectedSecondMajor.includes(degree.Major)) {
          console.warn(t('majorPage.majorAlreadySelected', 'This major has already been selected.'));
          return;
        }

        let secondMajorCourses = await fetchCoursesFromDatabase(degree.Major, fetchedCourseData, fetchedMajors);

        secondMajorCourses = secondMajorCourses.map((course) => ({
          ...course,
          degreeName: degree.Major,
        }));

        setSelectedDegreeCourses((prevCourses) => {
          const newCourses = secondMajorCourses.filter(
            (newCourse) =>
              !prevCourses.some(
                (prevCourse) => prevCourse.courseInfo_courseNumber === newCourse.courseInfo_courseNumber
              )
          );
          return [...prevCourses, ...newCourses];
        });

        setSelectedSecondMajor((prevMajors) => [...prevMajors, degree.Major]);
      } else {
        setSelectedSecondMajor([]);
      }
    } catch (error) {
      console.error(t('majorPage.errorFetchingSecondMajorCourses', 'Error fetching second major courses:'), error);
    }
  };

  const handleRemoveSecondMajor = (majorName) => {
    setSelectedSecondMajor((prevMajors) => prevMajors.filter((major) => major !== majorName));

    setSelectedDegreeCourses((prevCourses) =>
      prevCourses.filter((course) => course.degreeName !== majorName)
    );
  };

  const [finalizedMilestones, setFinalizedMilestones] = useState([]);
  const [milestonesByYearAndSemester, setMilestonesByYearAndSemester] = useState([]);

  const organizePrereqCourses = () => {
    const prereqCourses = {};
    for (let i = 1; i <= 4; i++) {
      if (!prereqCourses[i]) {
        prereqCourses[i] = [];
      }
      prereqCourses[i].push(...middleschoolClasses);

      activeTabCourses.forEach((course) => {
        const year = course.courseYear || 'Uncategorized';
        if (year < i) {
          prereqCourses[i].push(course.courseInfo_courseNumber);
        }
      });
    }

    return prereqCourses;
  };

  const prereqClasses = organizePrereqCourses();
  function getYearClassName(yearNumber, termKey) {
    if (!surveyAnswers?.SchoolStart?.year) return '';
    const startYear = Number(surveyAnswers.SchoolStart.year);
    
    // Convert the termKey into a friendly name, e.g. "Fall", "Spring", "Summer"
    const friendlyName = termMap[String(termKey)] || termKey;
    let offset = 0;
    if (friendlyName.toLowerCase() !== 'fall') {
      offset = 1;
    }
    const calculatedYear = startYear + (yearNumber - 1) + offset;
  
    return `${calculatedYear} ${friendlyName}`;
  }

  const getTotalCreditsForSemester = (courses, yearNumber, semester) => {
    if (!courses || courses.length === 0) {
        return 0;
    }

    let totalCredits = courses.reduce((total, course) => {
        let courseCredits = parseFloat(course.Credits);
        const additionalCredits = (course.courseType === 'Elective' || course.courseType === 'Requirement') ? 3 : 0;
        const validCredits = !isNaN(courseCredits) ? courseCredits : 0;

        return total + validCredits + additionalCredits;
    }, 0);

    return totalCredits;
  };

  const handleKeepCourse = async (coursesToKeep, yr, sem) => {
    try {
      console.log('Keep Course: ', coursesToKeep, yr, sem);
      if (currentUser && currentUser.uid) {
        const userDocRef = db.collection('users').doc(currentUser.uid);
  
        await db.runTransaction(async (transaction) => {
          const userDoc = await transaction.get(userDocRef);
          if (userDoc.exists) {
            const userData = userDoc.data();
            const previousSchedule = userData.schedules[activeSchedule] || {};
            const updateObj = { ...previousSchedule };
            const yearKey = `year${yr}`;
  
            // Use the mapping logic from your dynamic terms 
            const semesterKey = termMap[sem];
  
            // Add these courses to Firestore
            const newCourseNumbers = coursesToKeep.map(
              (course) => course.courseInfo_courseNumber
            );

            
  
            const updatedSchedule = {
              ...updateObj,
              [yearKey]: {
                ...updateObj[yearKey],
                [semesterKey]: {
                  ...(
                    updateObj[yearKey] &&
                    updateObj[yearKey][semesterKey]
                      ? updateObj[yearKey][semesterKey]
                      : {}
                  ),
                  courses: (
                    updateObj[yearKey] &&
                    updateObj[yearKey][semesterKey] &&
                    updateObj[yearKey][semesterKey].courses
                  )
                    ? updateObj[yearKey][semesterKey].courses.concat(newCourseNumbers)
                    : newCourseNumbers,
                },
              },
            };
  
            transaction.update(userDocRef, {
              [`schedules.${activeSchedule}`]: updatedSchedule,
            });
          } else {
            console.error(
              'User data not found for the given userId:', 
              currentUser.uid
            );
            throw new Error('User data not found');
          }
        });
        const coursesToAdd = coursesToKeep.map((course) => course.courseInfo_courseNumber);
  
        // **Update local state** for *all* selected courses:
        const updatedCourses = activeTabCourses.map((c) => {
          const courseIdentifier =
            c.courseType === 'Elective' || c.courseType === 'Requirement'
              ? c.Requirements
              : c.courseInfo_courseNumber;
          if (coursesToAdd.includes(courseIdentifier)) {
            return {
              ...c,
              isKept: true,
            };
          }
          return c;
        });
  
        setActiveTabCourses(updatedCourses);
      } else {
        console.error('Error: currentUser or currentUser.uid is not defined');
      }
    } catch (error) {
      console.error('Error keeping elective course:', error);
    }
  };
  

  useEffect(() => {
    if (tabs.length > 0 && typeof tabs[activeTab] !== 'undefined') {
      const activeTabMajors = tabs[activeTab].split(' / ').map((major) => major.trim());
      setSelectedMajor(activeTabMajors);
  
      fetchCoursesFromDatabase(activeTabMajors, fetchedCourseData, fetchedMajors)
        .then((newCourses) => {
          setActiveTabCourses(Array.isArray(newCourses) ? newCourses : []);
  
          // Update maxCourseYear based on the newly fetched courses
          const maxYear = Array.isArray(newCourses)
            ? newCourses.reduce((max, course) => Math.max(max, course.courseYear || 1), 1)
            : 1;
          setMaxCourseYear(prevMax => Math.max(prevMax, maxYear));
        })
        .catch((error) => {
          console.error(t('majorPage.errorFetchingCourses', 'Error fetching courses:'), error);
        });
    }
  }, [tabs, activeTab]);
  

  const [selectedCourses, setSelectedCourses] = useState([]);

  const onCourseSelection = (course, isSelected) => {
    setSelectedCourses((prevSelectedCourses) => {
      if (isSelected) {
        if (!prevSelectedCourses.some((c) => c.courseInfo_courseNumber === course.courseInfo_courseNumber)) {
          return [...prevSelectedCourses, course];
        }
      } else {
        return prevSelectedCourses.filter((c) => c.courseInfo_courseNumber !== course.courseInfo_courseNumber);
      }
      return prevSelectedCourses;
    });
  };

  const fetchMinorCoursesFromDatabase = async (minorName) => {
    try {
      const minor = fetchedMinors[minorName];

      if (!minor || typeof minor !== 'object') {
        console.log(t('majorPage.minorNotFoundOrInvalid', 'Minor not found or invalid'));
        return [];
      }

      const findCredits = (course) => {
        if (course) {
          const match = fetchedCourseData.find(
            (fetchedCourse) =>
              fetchedCourse.courseInfo_courseNumber === course.courseInfo_courseNumber
          );
          return match ? parseFloat(match.Credits) || 0 : 0;
        }
        return 0;
      };
      

      const findName = (course) => {
        if (course) {
          if (course.Name) {
            return course.Name;
          }
          const match = fetchedCourseData.find(
            (fetchedCourse) => fetchedCourse.courseInfo_courseNumber === course
          );
          return match ? match.courseInfo_courseName : null;
        }
      };

      const findDescription = (course) => {
        if (course) {
          const match = fetchedCourseData.find(
            (fetchedCourse) => fetchedCourse.courseInfo_courseNumber === course.courseInfo_courseNumber
          );

          console.log(t('majorPage.nameMatch', 'Name match:'), match);
          return match ? match.Description : null;
        }
      };

      let arr = Object.entries(minor).map(([courseNumber, courseData]) => ({
        courseInfo_courseNumber: courseNumber,
        courseYear: courseData.courseYear || '',
        CourseSem: courseData.CourseSem || '',
        courseInfo_courseName: findName(courseNumber),
        Credits: findCredits(courseNumber),
        Requirements: courseData.Requirements,
        Possibilities: courseData.Possibilities,
        Description: findDescription(courseData),
      }));
      return arr;
    } catch (error) {
      console.error(t('majorPage.errorFetchingMinorCourses', 'Error fetching minor courses from the Realtime Database:'), error);
      return [];
    }
  };

  const handleMinorSelection = async (minorName) => {
    try {
      if (minorName) {
        if (selectedMinor.includes(minorName)) {
          console.warn(t('majorPage.minorAlreadySelected', 'This minor has already been selected.'));
          return;
        }

        let minorCourses = await fetchMinorCoursesFromDatabase(minorName);

        minorCourses = minorCourses.map((course) => ({
          ...course,
          minorName: minorName,
        }));

        setSelectedMinorCourses((prevCourses) => {
          const newCourses = minorCourses.filter(
            (newCourse) =>
              !prevCourses.some(
                (prevCourse) => prevCourse.courseInfo_courseNumber === newCourse.courseInfo_courseNumber
              )
          );
          return [...prevCourses, ...newCourses];
        });

        setSelectedMinor((prevMinors) => [...(prevMinors || []), minorName]);
      } else {
        setSelectedMinorCourses([]);
        setSelectedMinor(null);
      }
    } catch (error) {
      console.error(t('majorPage.errorFetchingMinorCourses', 'Error fetching minor courses:'), error);
    }
  };

  const handleRemoveMinor = (minorName) => {
    setSelectedMinor((prevMinors) => prevMinors.filter((minor) => minor !== minorName));

    setSelectedMinorCourses((prevCourses) =>
      prevCourses.filter((course) => course.minorName !== minorName)
    );
  };

  const SelectedMinorBox = ({ minorName, onRemove }) => (
    <div className="bg-yellow-100 bg-opacity-50 text-yellow-500 mb-2 font-semibold rounded-lg text-sm px-2 py-1 mr-2 flex items-center">
      {minorName}
      <button className="ml-2 text-yellow-500 hover:text-yellow-900" onClick={onRemove}>
        <FaTimes />
      </button>
    </div>
  );

  const SelectedSecondMajorBox = ({ majorName, onRemove }) => (
    <div className="bg-green-100 bg-opacity-50 mb-2 text-green-500 font-semibold rounded-lg text-sm px-2 py-1 mr-2 flex items-center">
      {majorName}
      <button className="ml-2 !text-green-500 hover:text-green-900" onClick={onRemove}>
        <FaTimes />
      </button>
    </div>
  );

  const [electiveYear, setElectiveYear] = useState([]);
  const [electiveSemester, setElectiveSemester] = useState([]);
  const [electivePossibilities, setElectivePossibilities] = useState([]);
  const [electiveRequirement, setElectiveRequirement] = useState([]);

  const handleElectiveClick = (category, year, semester, requirement) => {
    console.log(t('majorPage.yearSemester', 'Year, semester'), year, semester);
    setSelectedElectiveCategory(category);
    setElectiveYear(year);
    setElectiveSemester(semester);
    setElectiveRequirement(requirement);
    setShowSidebar(true);
  };
  const [activeSchedule, setActiveSchedule] = useState([]);

  const fetchAllMajorsCourses = async (majors) => {
    try {
      console.log(t('majorPage.majors', 'Majors:'), majors);
  
      const majorsArray = Array.isArray(majors) ? majors : [majors];
  
      const allCourses = [];
      let calculatedMaxYear = 1;
  
      for (const major of majorsArray) {
        const courses = await fetchCoursesFromDatabase(major, fetchedCourseData, fetchedMajors);
        allCourses.push(...courses);
  
        // Update the maximum courseYear
        const majorMaxYear = courses.reduce((max, course) => Math.max(max, course.courseYear || 1), 1);
        calculatedMaxYear = Math.max(calculatedMaxYear, majorMaxYear);
      }
  
      console.log(t('majorPage.allCourses', 'All Courses:'), allCourses);
      setMaxCourseYear(calculatedMaxYear); // Update the state here as well
      return allCourses;
    } catch (error) {
      console.log(t('majorPage.majorsError', 'MajorsError:'), majors);
      console.error(t('majorPage.errorFetchingCoursesForAllMajors', 'Error fetching courses for all majors:'), error);
      return [];
    }
  };
  
  const [credits, setCredits] = useState(0);
  const [upperCredits, setUpperCredits] = useState(0);

  useEffect(() => {
    const fetchUserData = async () => {
      try {
        const user = auth.currentUser;
        setCurrentUser(user);
        if (user) {
          const userDocRef = db.collection('users').doc(user.uid);
          const userDoc = await userDocRef.get();
          if (userDoc.exists) {
            const userData = userDoc.data();
            const activeSchedule = userData.activeTab;
            setActiveSchedule(activeSchedule);
            const numYearsFromDB = userData?.surveyAnswers?.SchoolEnd?.year - userData?.surveyAnswers?.SchoolStart?.year || 4;
            setNumYears(numYearsFromDB);
            const majorPathwayData = pathwayGroupingsData.filter((major) => major.pathwayType === 'Major');
            const userMajorPathwayDataArray = majorPathwayData.filter((major) => selectedMajor1.includes(major.pathwayName));
            const totalCredits = userMajorPathwayDataArray.reduce((sum, majorData) => sum + (parseFloat(majorData.OverallCR) || 0), 0);
            const totalUpperCredits = userMajorPathwayDataArray.reduce((sum, majorData) => sum + (parseFloat(majorData.UDCR) || 0), 0);
            setCredits(totalCredits);
            setUpperCredits(totalUpperCredits);
            console.log(t('majorPage.userPathwayData', 'User pathway data:'), userMajorPathwayDataArray);
            const savedMilestones = userData.finalizedMilestones || {};
            setCheckedMilestones(savedMilestones);
            if (!userData.finalizedMilestones) {
              let defaultMilestones = {};
              for (let i = 1; i <= numYears; i++) {
                // Now, initialize each year using dynamic term keys
                defaultMilestones[`year${i}`] = {};
                sortedTermKeys.forEach((tk) => {
                  defaultMilestones[`year${i}`][tk] = { milestones: [] };
                });
                await userDocRef.update({ finalizedMilestones: defaultMilestones });
                userData.finalizedMilestones = defaultMilestones;
              }
            }
            const backendSurveyAnswers = userData.surveyAnswers || [];
            setSurveyAnswers(backendSurveyAnswers);
            if (tabs.length === 0) {
              const userMajors = userData.surveyAnswers.Majors;
              if (Array.isArray(userMajors)) {
                const tabName = userMajors.join(' / ');
                setTabs([tabName]);
                setSelectedMajor(userMajors);
              }
            }
            const keptCourseNumbers = getUserKeptCourses(userData.schedules[activeSchedule]);
            setFinalizedOrganizedCourses(userData.schedules[activeSchedule])
            const keptMilestoneNumbers = getUserKeptMilestones(userData.finalizedMilestones);
            // (Assume you set completedMilestones somewhere if needed)
            const courses = await fetchAllMajorsCourses(selectedMajor1);
            console.log(t('majorPage.totDat', 'TotDat:'), courses, selectedMajor1);
            const milestones = await fetchMilestonesFromDatabase(selectedMajor1);
            setMilestones(milestones);
            const fetchedElectives = fetchElectivesFromDatabase(selectedMajor1, fetchedElective);
            setElectiveData(fetchedElectives);
          
            const filteredMinorCourses = selectedMinorCourses.filter(
              (minorCourse) =>
                !courses.some(
                  (course) => course.courseInfo_courseNumber === minorCourse.courseInfo_courseNumber
                )
            );

            const filteredDegreeCourses = selectedDegreeCourses.filter(
              (degreeCourse) =>
                !courses.some(
                  (course) => course.courseInfo_courseNumber === degreeCourse.courseInfo_courseNumber
                )
            );
            const allCourses = courses.concat(filteredMinorCourses);
            const allCoursesWithMajor = allCourses.concat(filteredDegreeCourses);
            const coursesWithKeptStatus = allCoursesWithMajor.map((course) => ({
              ...course,
              isKept: keptCourseNumbers.includes(course.courseInfo_courseNumber),
            }));
            console.log('CoursesWithKeptStatus: ', coursesWithKeptStatus);
            console.log(t('majorPage.activeTabCourses', 'Active Tab Courses:'), activeTabCourses);
            setActiveTabCourses(coursesWithKeptStatus);
            console.log(t('majorPage.activeTabCoursesLog', 'Active tab courses:'), courses, allCourses, activeTabCourses);
          }
        }
      } catch (error) {
        console.error(t('majorPage.errorFetchingUserData', 'Error fetching user data:'), error);
      }
      setLoading(false);
    };
    fetchUserData();
  }, [
    selectedMajor1,
    fetchedCourseData,
    fetchedElective,
    finalizedMilestones,
    activeTab,
    tabs,
    selectedMinor,
    selectedMinorCourses,
    selectedSecondMajor,
    pathwayGroupingsData,
  ]);

  const handleTabClick = useCallback((index) => {
    setActiveTab(index);
  }, []);  

  const handleRemoveTab = async (indexToRemove) => {
    const updatedTabs = tabs.filter((_, index) => index !== indexToRemove);

    let newActiveTab = activeTab;
    if (indexToRemove === activeTab) {
      if (indexToRemove === tabs.length - 1) {
        newActiveTab = indexToRemove - 1;
        handleTabClick(newActiveTab);
      } else {
        newActiveTab = indexToRemove;
        handleTabClick(newActiveTab);
      }
    } else if (indexToRemove < activeTab) {
      newActiveTab = activeTab - 1;
      handleTabClick(newActiveTab);
    }

    setTabs(updatedTabs);
    setSelectedMajor(updatedTabs[newActiveTab]);

    if (updatedTabs.length > 0) {
      const newCourses = await fetchCoursesFromDatabase(updatedTabs[newActiveTab], fetchedCourseData, fetchedMajors);
      setActiveTabCourses(Array.isArray(newCourses) ? newCourses : []);
      handleTabClick(newActiveTab);
    }
  };

  useEffect(() => {
    if (tabs.length > 0 && typeof tabs[activeTab] !== 'undefined') {
      const activeTabMajors = tabs[activeTab].split(' / ').map((major) => major.trim());
      setSelectedMajor(activeTabMajors);

      fetchCoursesFromDatabase(activeTabMajors, fetchedCourseData, fetchedMajors)
        .then((newCourses) => {
          setActiveTabCourses(Array.isArray(newCourses) ? newCourses : []);
        })
        .catch((error) => {
          console.error(t('majorPage.errorFetchingCourses', 'Error fetching courses:'), error);
        });
    }
  }, [tabs, activeTab]);

  const [selectedElectiveCourses, setSelectedElectiveCourses] = useState([]);

  // Updated handleKeepElectiveCourse now uses term numbers (e.g. 1,2,3,…) instead of "S1"/"S2"
  const handleKeepElectiveCourse = async (course, yr, sem) => {
    try {
      console.log('Keep Elective Course:', course, yr, sem);
      if (currentUser && currentUser.uid) {
        const userDocRef = db.collection('users').doc(currentUser.uid);
        await db.runTransaction(async (transaction) => {
          const userDoc = await transaction.get(userDocRef);
          if (userDoc.exists) {
            const userData = userDoc.data();
            const previousSchedule = userData.schedules[activeSchedule] || {};
            const updateObj = { ...previousSchedule };
            const yearKey = `year${yr}`;
            // Now simply use the term number (as a string)
            let semesterKey = termMap[sem];
            const updatedSchedule = {
              ...updateObj,
              [yearKey]: {
                ...updateObj[yearKey],
                [semesterKey]: {
                  ...(updateObj[yearKey] && updateObj[yearKey][semesterKey]
                    ? updateObj[yearKey][semesterKey]
                    : {}),
                  courses: (updateObj[yearKey] &&
                    updateObj[yearKey][semesterKey] &&
                    updateObj[yearKey][semesterKey].courses
                    ? updateObj[yearKey][semesterKey].courses.concat([course.courseInfo_courseNumber])
                    : [course.courseInfo_courseNumber]),
                },
              },
            };
            setFinalizedOrganizedCourses(updatedSchedule)
            transaction.update(userDocRef, {
              [`schedules.${activeSchedule}`]: updatedSchedule,
            });
          } else {
            console.error(t('majorPage.userDataNotFound', 'User data not found for the given userId:'), currentUser.uid);
            throw new Error(t('majorPage.userDataNotFoundError', 'User data not found'));
          }
        });
        const updatedCourses = activeTabCourses.map((c) =>
          c.courseInfo_courseNumber === course.courseInfo_courseNumber
            ? { ...c, isKept: true }
            : c
        );
        console.log('UpdatedCourses: ', updatedCourses);
        setActiveTabCourses(updatedCourses);
      } else {
        console.error(t('majorPage.currentUserNotDefined', 'Error: currentUser or currentUser.uid is not defined'));
      }
    } catch (error) {
      console.error(t('majorPage.errorKeepingElectiveCourse', 'Error keeping elective course:'), error);
    }
  };
  
  const closeDegreePlanSearchModal = () => {
    setShowDegreePlanSearchModal(false);
    setSearchQuery('');
    setSelectedFaculty(null);
    // Create a new object without the "Electives" key
    const { Electives, ...filteredMajors } = fetchedMajors;
    setFilteredFetchedMajors(filteredMajors);
  };


  const closeAdditionalMajorSearchModal = () => {
    setShowAdditionalMajorSearchModal(false);
    setSearchQuery('');
    setSelectedFaculty(null);
    // Create a new object without the "Electives" key
    const { Electives, ...filteredMajors } = fetchedMajors;
    setFilteredFetchedMajors(filteredMajors);
  };

  const closeAdditionalMinorSearchModal = () => {
    setShowCloseAdditionalMinorSearchModal(false);
    setSearchQuery('');
    setFilteredMinors(fetchedMinors);
  };

  const [showAdditionalMajorSearchModal, setShowAdditionalMajorSearchModal] = useState(false);
  const [showCloseAdditionalMinorSearchModal, setShowCloseAdditionalMinorSearchModal] = useState(false);

  const modalRef = useRef();
  const modalRef1 = useRef();
  const modalRef2 = useRef();

  const handleClickOutside = (event) => {
    if (modalRef.current && !modalRef.current.contains(event.target)) {
      setShowDegreePlanSearchModal(false);
      setSearchQuery('');
    }
    if (modalRef1.current && !modalRef1.current.contains(event.target)) {
      setShowAdditionalMajorSearchModal(false);
      setSearchQuery('');
    }
    if (modalRef2.current && !modalRef2.current.contains(event.target)) {
      setShowCloseAdditionalMinorSearchModal(false);
      setSearchQuery('');
    }
  };

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  if (loading) {
    return (
      <div role="status">
        <svg
          aria-hidden="true"
          className="w-8 h-8 text-gray-200 animate-spin dark:text-gray-600 fill-blue-600"
          viewBox="0 0 100 101"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
            fill="currentColor"
          />
          <path
            d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
            fill="currentFill"
          />
        </svg>
        <span className="sr-only">{t('majorPage.loading', 'Loading...')}</span>
      </div>
    );
  }

  const usedTermKeys = sortedTermKeys.filter((termKey) =>
    activeTabCourses.some((course) => Number(course.CourseSem) === Number(termKey))
  );
  

  return (
    <DndProvider backend={HTML5Backend}>
      <div className="semester-card-container mb-3">
        <NavBar />
        {currentUser ? (
        
          <div className="semester-card-main-content flex mt-20">
            {showIncompleteDataWarningModal && (
              <IncompleteDataWarningModal
                onClose={handleCloseIncompleteDataWarningModal}
              />
            )}
            
            <div className="flex-1 flex flex-col space-y-2">
              {/* Tabs Section */}
              {fetchedCourseData && filteredFetchedMajors && (
                <div className="flex border-b border-gray-200 dark:border-gray-700 mb-2 items-center">
                  <div className="flex items-center flex-1 overflow-x-auto custom-scrollbar space-x-2">
                    <ul className="flex flex-nowrap">
                      {tabs.map((tab, index) => (
                        <li
                          key={index}
                          className="relative mr-2 flex items-center flex-shrink-0"
                        >
                          <div className="flex items-center">
                            <button
                              className={`inline-block py-2 px-3 border-b-2 rounded-t-lg flex items-center ${
                                activeTab === index
                                  ? "border-blue-500 text-blue-500"
                                  : "border-transparent hover:text-gray-600 hover:border-gray-300 dark:hover:text-gray-300"
                              }`}
                              onClick={() => handleTabClick(index)}
                            >
                              {tab}
                              {tabs.length > 1 && (
                                <button
                                  className="ml-2 text-black hover:text-red-700"
                                  onClick={() => handleRemoveTab(index)}
                                >
                                  &times;
                                </button>
                              )}
                            </button>
                          </div>
                        </li>
                      ))}
                    </ul>
                    <button
                      onClick={() => setShowDegreePlanSearchModal(true)}
                      className="text-black mr-2 hover:text-gray-700 text-lg p-0 ml-2 flex items-center"
                    >
                      +
                    </button>
                  </div>
                  <button
                    onClick={() => setShowIncompleteDataWarningModal(true)}
                    className="text-red-600 hover:text-red-800 text-sm font-semibold ml-auto p-0 flex items-center"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      className="h-4 w-4"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M10.29 3.86l-8.46 14.7c-.7 1.22.17 2.44 1.53 2.44h16.88c1.36 0 2.23-1.22 1.53-2.44l-8.46-14.7a1.6 1.6 0 00-2.76 0z"
                      />
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 9v4m0 4h.01"
                      />
                    </svg>
                  </button>
                </div>
              )}
  
              {/* Credits Overview and Add Major/Minor Buttons */}
              <div className="flex items-center justify-between text-sm font-semibold">
                <div className="flex space-x-2">
                  <div className="flex items-center bg-blue-100 bg-opacity-50 rounded px-2 py-1 text-blue-700">
                    <FaBook className="mr-2" />
                    <span>{t("majorPage.totalCredits", "Total Credits:")}</span>
                    <span className="ml-1 text-blue-800">
                      {credits || t("majorPage.na", "N/A")}
                    </span>
                  </div>
                  <div className="flex items-center bg-green-100 bg-opacity-50 rounded px-2 py-1 text-green-700">
                    <FaBookBookmark className="mr-2" />
                    <span>{t("majorPage.upperDivisionCredits", "Upper Division Credits:")}</span>
                    <span className="ml-1 text-green-800">
                      {upperCredits || t("majorPage.na", "N/A")}
                    </span>
                  </div>
                </div>
                <div className="flex items-center">
                  <button
                    type="button"
                    onClick={() => setShowAdditionalMajorSearchModal(true)}
                    className="inline-flex items-center px-3 py-1.5 text-xs font-medium text-gray-900 bg-white border border-gray-200 rounded-s-lg hover:bg-gray-100 hover:text-blue-700 focus:z-10 focus:ring-2 focus:ring-blue-700 focus:text-blue-700 dark:bg-gray-800 dark:border-gray-700 dark:text-white dark:hover:text-white dark:hover:bg-gray-700 dark:focus:ring-blue-500 dark:focus:text-white"
                  >
                    <FaGraduationCap className="w-3 h-3 me-2" />
                    {t("majorPage.addMajors", "Add major(s)")}
                  </button>
                  {fetchedMinors && (
                    <button
                      type="button"
                      onClick={() => setShowCloseAdditionalMinorSearchModal(true)}
                      className="inline-flex items-center px-3 py-1.5 text-xs font-medium text-gray-900 bg-white border border-gray-200 rounded-e-lg hover:bg-gray-100 hover:text-blue-700 focus:z-10 focus:ring-2 focus:ring-blue-700 focus:text-blue-700 dark:bg-gray-800 dark:border-gray-700 dark:text-white dark:hover:text-white dark:hover:bg-gray-700 dark:focus:ring-blue-500 dark:focus:text-white"
                    >
                      <FaBookOpen className="w-3 h-3 me-2" />
                      {t("majorPage.addMinors", "Add minor(s)")}
                    </button>
                  )}
                </div>
              </div>
  
              {/* Display Selected Minors and Second Majors */}
              <div className="flex flex-wrap">
                {selectedSecondMajor.map((majorName) => (
                  <SelectedSecondMajorBox
                    key={majorName}
                    majorName={majorName}
                    onRemove={() => handleRemoveSecondMajor(majorName)}
                  />
                ))}
                {selectedMinor.map((minorName) => (
                  <SelectedMinorBox
                    key={minorName}
                    minorName={minorName}
                    onRemove={() => handleRemoveMinor(minorName)}
                  />
                ))}
              </div>
  
              {/* Semester Cards */}
              {Array.from({ length: maxCourseYear }, (_, i) => i + 1).map(
                (yearNumber) => (
                  <div
                    key={yearNumber}
                    style={{
                      display: "grid",
                      gap: "0.5rem",
                      width: "100%",
                      gridTemplateColumns: `repeat(${usedTermKeys.length}, minmax(200px, 1fr))`,
                    }}
                  >
                    {usedTermKeys.map((termKey) => {
                      const nTK = Number(termKey)
                      const friendlyTerm = termMap[termKey];
                      return (
                        <div
                          key={`${termKey}-${yearNumber}`}
                          className="semester-section border rounded-lg p-3 bg-white shadow-md space-y-1"
                        >
                          <div className="flex justify-between items-center mb-0.5 ml-2">
                            <h3 className="text-lg font-medium mb-1">
                              {getYearClassName(yearNumber, termKey)}
                            </h3>
                            <span className="bg-purple-100 text-purple-800 text-xs font-medium px-1 py-0.5 mb-2 rounded border border-purple-400">
                              {Number.isFinite(
                                getTotalCreditsForSemester(
                                  activeTabCourses.filter(
                                    (course) =>
                                      course.CourseSem === nTK &&
                                      course.courseYear === yearNumber
                                  ),
                                  yearNumber,
                                  termKey
                                )
                              )
                                ? getTotalCreditsForSemester(
                                    activeTabCourses.filter(
                                      (course) =>
                                        course.CourseSem === nTK &&
                                        course.courseYear === yearNumber
                                    ),
                                    yearNumber,
                                    termKey
                                  )
                                : 0}{" "}
                              {t("majorPage.credits", "Credits")}
                            </span>
                          </div>
                          <FinalizedCoursesTwo
                            finalizedCourses={
                              activeTabCourses.filter(
                              (course) =>
                                course.CourseSem === nTK &&
                                course.courseYear === yearNumber
                            )}
                            semesterCourses={
                              fetchedCourseData.filter(courseObj =>
                                (finalizedOrganizedCourses?.[`year${yearNumber}`]?.[termMap?.[nTK]]?.courses || [])
                                  .includes(courseObj.courseInfo_courseNumber)
                              )
                            }
                            prereqClasses={prereqClasses}
                            activeSchedule={activeSchedule}
                            transformedMajorRecData={transformedMajorRecData}
                            fetchedCourseData={fetchedCourseData}
                            semester={termKey}
                            yearNumber={yearNumber}
                            onCourseSelection={(courseId, isSelected) =>
                              onCourseSelection(
                                courseId,
                                isSelected,
                                yearNumber,
                                termKey
                              )
                            }
                            onKeepCourse={handleKeepCourse}
                            electiveData={electiveData}
                            onElectiveClick={handleElectiveClick}
                            showSidebar={showSidebar}
                            tabs={tabs}
                            activeTab={activeTab}
                            selectedMajor1={selectedMajor1}
                            selectedElectiveCategory={selectedElCat}
                            majorRecData={transformedMajorRecData}
                            finalizedCourses1={activeTabCourses}
                            possibilities={electivePossibilities}
                            handleKeepElectiveCourse={handleKeepElectiveCourse}
                          />
                        </div>
                      );
                    })}
                  </div>
                )
              )}
            </div>
            </div>
          ) : (
            <p>{t("majorPage.loadingUserData", "Loading user data...")}</p>
          )}
        </div>
  
        {/* Modals for Adding Degrees and Minors */}
        {/* Modal for Adding a Degree */}
        {showDegreePlanSearchModal && (
          <div className="fixed inset-0 z-50 flex">
            <div
              className="flex-1 h-full bg-black bg-opacity-50"
              onClick={closeDegreePlanSearchModal}
            ></div>
            <div
              className="relative w-1/3 max-w-full h-full bg-gray-50 shadow-lg overflow-y-auto"
              ref={modalRef}
            >
              <div className="flex justify-between items-center p-4 md:p-5 border-b border-gray-200">
                <h3 className="text-xl font-semibold center text-black">
                  {t("majorPage.selectDegree", "Select a Degree")}
                </h3>
                <button
                  type="button"
                  onClick={closeDegreePlanSearchModal}
                  className="text-gray-400 bg-transparent hover:bg-gray-200 hover:text-gray-900 rounded-lg text-sm w-8 h-8 absolute top-2.5 right-2.5 inline-flex items-center justify-center"
                >
                  <svg
                    className="w-3 h-3"
                    aria-hidden="true"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 14 14"
                  >
                    <path
                      stroke="currentColor"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="m1 1 6 6m0 0 6 6M7 7l6-6M7 7l-6 6"
                    />
                  </svg>
                  <span className="sr-only">
                    {t("majorPage.closeMenu", "Close menu")}
                  </span>
                </button>
              </div>
              <div className="p-4 md:p-5 space-y-4 text-left">
                <div className="mb-2 bg-white p-4 rounded border border-gray-200">
                  <div className="mb-4 relative">
                    <label
                      htmlFor="searchQuery"
                      className="block text-sm font-medium text-gray-700 mb-1"
                    >
                      {t("majorPage.search", "Search:")}
                    </label>
                    <div className="relative">
                      <input
                        type="text"
                        id="searchQuery"
                        value={searchQuery}
                        onChange={handleSearchQueryChange}
                        className="form-control w-full pl-10 rounded border-gray-300 shadow-sm"
                      />
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <svg
                          className="w-5 h-5 text-gray-500"
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path
                            fillRule="evenodd"
                            d="M12.9 14.32a8 8 0 111.414-1.414l5.387 5.386-1.415 1.414-5.386-5.386zm-4.9-1.32a5 5 0 100-10 5 5 0 000 10z"
                            clipRule="evenodd"
                          />
                        </svg>
                      </div>
                    </div>
                  </div>
                  <div className="mb-2">
                    <label
                      htmlFor="facultyFilter"
                      className="block text-sm font-medium text-gray-700 mb-1"
                    >
                      {t("majorPage.filterByFaculty", "Filter by Faculty:")}
                    </label>
                    <select
                      id="facultyFilter"
                      value={selectedFaculty || ""}
                      onChange={(e) => handleFacultyFilterChange(e.target.value)}
                      className="form-control w-full rounded border-gray-300 shadow-sm"
                    >
                      <option value="">
                        {t("majorPage.allFaculties", "All Faculties")}
                      </option>
                      {faculties.map((faculty) => (
                        <option key={faculty} value={faculty}>
                          {faculty}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
                <ul className="space-y-0">
                  {Object.keys(filteredFetchedMajors).map((major, index) => (
                    <li
                      key={major}
                      className={`w-full text-base leading-relaxed text-black cursor-pointer px-4 py-3 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-300 ${
                        index !== Object.keys(filteredFetchedMajors).length - 1
                          ? "border-b border-gray-200"
                          : ""
                      }`}
                      onClick={() => handleDegreeSelection({ Major: major })}
                    >
                      {major}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        )}
  
        {/* Modal for Adding a Second Degree */}
        {showAdditionalMajorSearchModal && (
          <div className="fixed inset-0 z-50 flex">
            <div
              className="flex-1 h-full bg-black bg-opacity-50"
              onClick={closeAdditionalMajorSearchModal}
            ></div>
            <div
              className="relative w-1/3 max-w-full h-full bg-gray-50 shadow-lg overflow-y-auto"
              ref={modalRef1}
            >
              <div className="flex justify-between items-center p-4 md:p-5 border-b border-gray-200">
                <h3 className="text-xl font-semibold text-black">
                  {t("majorPage.selectSecondDegree", "Select a Second Degree")}
                </h3>
                <button
                  type="button"
                  onClick={closeAdditionalMajorSearchModal}
                  className="text-gray-400 bg-transparent hover:bg-gray-200 hover:text-gray-900 rounded-lg text-sm w-8 h-8 absolute top-2.5 right-2.5 inline-flex items-center justify-center"
                >
                  <svg
                    className="w-3 h-3"
                    aria-hidden="true"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 14 14"
                  >
                    <path
                      stroke="currentColor"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="m1 1 6 6m0 0 6 6M7 7l6-6M7 7l-6 6"
                    />
                  </svg>
                  <span className="sr-only">
                    {t("majorPage.closeMenu", "Close menu")}
                  </span>
                </button>
              </div>
              <div className="p-4 md:p-5 space-y-4 text-left">
                <div className="mb-2 bg-white p-4 rounded border border-gray-200">
                  <div className="mb-4 relative">
                    <label
                      htmlFor="searchQuery"
                      className="block text-sm font-medium text-gray-700 mb-1"
                    >
                      {t("majorPage.search", "Search:")}
                    </label>
                    <div className="relative">
                      <input
                        type="text"
                        id="searchQuery"
                        value={searchQuery}
                        onChange={handleSearchQueryChange}
                        className="form-control w-full pl-10 rounded border-gray-300 shadow-sm"
                      />
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <svg
                          className="w-5 h-5 text-gray-500"
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path
                            fillRule="evenodd"
                            d="M12.9 14.32a8 8 0 111.414-1.414l5.387 5.386-1.415 1.414-5.386-5.386zm-4.9-1.32a5 5 0 100-10 5 5 0 000 10z"
                            clipRule="evenodd"
                          />
                        </svg>
                      </div>
                    </div>
                  </div>
                  <div className="mb-2">
                    <label
                      htmlFor="facultyFilter"
                      className="block text-sm font-medium text-gray-700 mb-1"
                    >
                      {t("majorPage.filterByFaculty", "Filter by Faculty:")}
                    </label>
                    <select
                      id="facultyFilter"
                      value={selectedFaculty || ""}
                      onChange={(e) => handleFacultyFilterChange(e.target.value)}
                      className="form-control w-full rounded border-gray-300 shadow-sm"
                    >
                      <option value="">
                        {t("majorPage.allFaculties", "All Faculties")}
                      </option>
                      {faculties.map((faculty) => (
                        <option key={faculty} value={faculty}>
                          {faculty}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
                <ul className="space-y-0">
                  {Object.keys(filteredFetchedMajors).map((major, index) => (
                    <li
                      key={major}
                      className={`w-full text-base leading-relaxed text-black cursor-pointer px-4 py-3 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-300 ${
                        index !== Object.keys(filteredFetchedMajors).length - 1
                          ? "border-b border-gray-200"
                          : ""
                      }`}
                      onClick={() => handleSecondMajorSelection({ Major: major })}
                    >
                      {major}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        )}
  
        {/* Modal for Adding a Second Minor */}
        {showCloseAdditionalMinorSearchModal && filteredMinors && (
          <div className="fixed inset-0 z-50 flex">
            <div
              className="flex-1 h-full bg-black bg-opacity-50"
              onClick={closeAdditionalMinorSearchModal}
            ></div>
            <div
              className="relative w-1/3 max-w-full h-full bg-gray-50 shadow-lg overflow-y-auto"
              ref={modalRef2}
            >
              <div className="flex justify-between items-center p-4 md:p-5 border-b dark:border-gray-600">
                <h3 className="text-xl font-semibold text-black">
                  {t("majorPage.selectMinor", "Select a Minor")}
                </h3>
                <button
                  type="button"
                  onClick={closeAdditionalMinorSearchModal}
                  className="text-gray-400 bg-transparent hover:bg-gray-200 hover:text-gray-900 rounded-lg text-sm w-8 h-8 absolute top-2.5 right-2.5 inline-flex items-center justify-center"
                >
                  <svg
                    className="w-3 h-3"
                    aria-hidden="true"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 14 14"
                  >
                    <path
                      stroke="currentColor"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="m1 1 6 6m0 0 6 6M7 7l6-6M7 7l-6 6"
                    />
                  </svg>
                  <span className="sr-only">
                    {t("majorPage.closeMenu", "Close menu")}
                  </span>
                </button>
              </div>
              <div className="p-4 md:p-5 space-y-4 text-left">
                <div className="mb-2 bg-white p-4 rounded border border-gray-200">
                  <label
                    htmlFor="searchQuery"
                    className="block text-sm font-medium text-gray-700 mb-1"
                  >
                    {t("majorPage.search", "Search:")}
                  </label>
                  <input
                    type="text"
                    id="searchQuery"
                    value={searchQuery}
                    onChange={(e) => {
                      setSearchQuery(e.target.value);
                      filterMinors(e.target.value);
                    }}
                    className="form-control mb-3 w-full rounded border-gray-300 shadow-sm"
                  />
                </div>
                <ul className="space-y-2">
                  {Object.keys(filteredMinors).map((minorName) => {
                    const isSelected = selectedMinor.includes(minorName);
                    return (
                      <li key={minorName}>
                        <button
                          className="w-full text-left mb-4 px-3 py-2 text-black border-b border-gray-200 rounded hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-300 flex items-center"
                          onClick={() => handleMinorSelection(minorName)}
                        >
                          <div
                            className={`flex items-center justify-center w-6 h-6 rounded-full mr-2 ${
                              isSelected
                                ? "bg-green-100 text-green-600"
                                : "bg-blue-100 text-blue-600"
                            }`}
                          >
                            {isSelected ? (
                              <svg
                                className="w-4 h-4"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                                xmlns="http://www.w3.org/2000/svg"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth="2"
                                  d="M5 13l4 4L19 7"
                                ></path>
                              </svg>
                            ) : (
                              <svg
                                className="w-4 h-4"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                                xmlns="http://www.w3.org/2000/svg"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth="2"
                                  d="M12 4v16m8-8H4"
                                ></path>
                              </svg>
                            )}
                          </div>
                          {minorName.replace("Minor: ", "")}
                        </button>
                      </li>
                    );
                  })}
                </ul>
              </div>
            </div>
          </div>
        )}
    </DndProvider>
  );
};

export default MajorPage;
